// src/components/ProtectedRoute.tsx

import React from 'react';
import { Navigate } from 'react-router-dom';
import { Result } from 'antd';
import { useAuthContext } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'super_admin' | 'communal_admin';
  requiredPermissions?: string[];
  requireAnyPermission?: boolean; // If true, user needs ANY of the permissions, not ALL
  fallbackPath?: string;
  showAccessDenied?: boolean;
}

/**
 * Route protection component for multi-tenant system
 * Handles role-based and permission-based access control
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermissions = [],
  requireAnyPermission = false,
  fallbackPath = '/login',
  showAccessDenied = true,
}) => {
  const {
    isAuthenticated,
    isInitialized,
    user,
    isSuperAdmin,
    isCommunalAdmin,
    isGroupAgent,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  } = useAuthContext();

  // Group agents should not access admin dashboard
  if (isGroupAgent()) {
    return (
      <Result
        status="403"
        title="Tableau de bord admin non disponible"
        subTitle={`Les agents de groupe doivent utiliser l'application mobile pour leurs opérations.`}
        extra={
          <div>
            <p>Veuillez utiliser l&apos;application mobile AVEC pour accéder à vos fonctions de gestion de groupe.</p>
          </div>
        }
      />
    );
  }

  // Wait for auth initialization
  if (!isInitialized) {
    return <div>Chargement...</div>;
  }

  // Check if user is authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check role requirements
  if (requiredRole) {
    let hasRequiredRole = false;

    switch (requiredRole) {
      case 'super_admin':
        hasRequiredRole = isSuperAdmin();
        break;
      case 'communal_admin':
        hasRequiredRole = isCommunalAdmin();
        break;
    }

    if (!hasRequiredRole) {
      if (!showAccessDenied) {
        return <Navigate to="/dashboard" replace />;
      }

      return (
        <Result
          status="403"
          title="403"
          subTitle="Désolé, vous n'êtes pas autorisé à accéder à cette page."
          extra={
            <div>
              <p>Rôle requis : {requiredRole}</p>
              <p>Votre rôle : {user.staffRole || 'Aucun rôle assigné'}</p>
            </div>
          }
        />
      );
    }
  }

  // Check permission requirements
  if (requiredPermissions.length > 0) {
    const hasRequiredPermissions = requireAnyPermission
      ? hasAnyPermission(requiredPermissions)
      : hasAllPermissions(requiredPermissions);

    if (!hasRequiredPermissions) {
      if (!showAccessDenied) {
        return <Navigate to="/dashboard" replace />;
      }

      return (
        <Result
          status="403"
          title="403"
          subTitle="Désolé, vous n'avez pas les permissions requises pour accéder à cette page."
          extra={
            <div>
              <p>Permissions requises : {requiredPermissions.join(', ')}</p>
              <p>Type d'accès : {requireAnyPermission ? 'N\'IMPORTE LAQUELLE' : 'TOUTES'}</p>
            </div>
          }
        />
      );
    }
  }

  // All checks passed, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;